/**
 * @file target-completion.tsx
 * @module app/modules/sales-management/admin/dashboard/chart/target-completion
 * @description Implements the target completion interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import React, { useEffect, useState } from "react";
import { Col, OverlayTrigger, Popover, Row, Tooltip } from "react-bootstrap";
import Chart from "react-apexcharts";
import { assistAdmin } from "../../../../../../api/apiEndPoints";
import APICallService from "../../../../../../api/apiCallService";
import Loader from "../../../../../../Global/loader";
import Method from "../../../../../../utils/methods";

const getBgColor = (percentage: number) => {
  if (percentage === 100) return "#15803d"; // green
  if (percentage > 50) return "#6366F1"; // blue
  return "#FF9800"; // orange
};

const renderTooltip = (props: any, item: any, remaining: number) => (
  <Popover
    {...props}
    id="popover-basic"
    className="border-0 shadow"
    style={{ ...props.style, minWidth: 200 }}
  >
    <Popover.Body className="p-0">
      {/* Name and Target */}
      <div className="mb-2 px-4 py-2">
        <span className="d-block text-black fs-16 fw-semibold mb-1">
          {item.name}
        </span>
        <span className="d-block text-muted fs-14 fw-semibold mb-1">
          {item.role[0]?.type === 1 ? 'Sales Manager' : 'Activation Manager'}
        </span>
        <span className="d-block fw-medium fs-16 text-dark">
          Tsh {Method.getGeneralizedAmount(item.currentSalesTarget?.target ?? 0)}
        </span>
      </div>

      <div className="separator border-gray-300"></div>

      {/* Achieved */}
      <div className="mb-2 px-4 py-2">
        <span className="d-block text-black fs-16 fw-semibold mb-1">
          Achieved
        </span>
        <span className="d-block fw-medium fs-16 text-dark">
          Tsh {Method.getGeneralizedAmount(item.currentSalesTarget?.achieved ?? 0)}
        </span>
      </div>

      <div className="separator border-gray-300"></div>

      {/* Remaining */}
      <div className="mb-2 px-4 py-2">
        <span className="d-block text-danger fs-16 fw-semibold mb-1">
          Remaining
        </span>
        <span className="d-block fw-medium fs-16 text-dark">
          Tsh {Method.getGeneralizedAmount(remaining ?? 0)}
        </span>
      </div>
    </Popover.Body>
  </Popover>
);

const TargetCompletion = () => {
  const [series] = useState([100]);
  const [loading, setLoading] = useState<boolean>(false);
  const [salesManagers, setSalesManagers] = useState<any>([]);

  useEffect(() => {
    fetchSalesUsers();
  }, []);


  const fetchSalesUsers = async () => {
    setLoading(true);
    let params: any = {
      pageNo: 1,
      limit: 0,
      sortKey: "createdAt",
      sortOrder: -1,
      needCount: false,
    };
    params["role[0]"] = 1;
    params["role[1]"] = 6;
    let apiService = new APICallService(
      assistAdmin.list,
      params,
      "",
      "",
      "",
      "",
      "",
      ""
    );
    let response = await apiService.callAPI();
    if (response.records) {
      setSalesManagers(response.records);
    } else {
      setSalesManagers([])
    }
    setLoading(false);
  };
  return (
    <Row className="align-items-center my-7">
      <Col xs={6}>
        <div
          className="px-5 py-5"
          style={{
            border: "0.5px solid #E0E0DF",
            borderRadius: "10px",
            background: "#F9F9F9",
          }}
        >
          <h3 className="fw-bolder fs-18 mb-4">
            Target completion by managers
          </h3>

          {/* Scroll container */}
          <div
            style={{
              maxHeight: salesManagers.length > 3 ? 180 : "auto",
              overflowY: salesManagers.length > 3 ? "auto" : "visible",
              paddingRight: 6,
            }}
          >
            {loading ? (
              <div className="text-center py-5">
                <Loader loading={loading} />
              </div>
            ) : (
              salesManagers.map((item: any, index: number) => {
                const remaining = Method.getGeneralizedAmount(Math.max(
                  0,
                  (item?.currentSalesTarget?.target ?? 0) -
                  (item?.currentSalesTarget?.achieved ?? 0)
                ))
                const percentage = item.currentSalesTarget?.target
                  ? Math.min(
                    Math.round(
                      ((Number(item.currentSalesTarget?.achieved) || 0) / item.currentSalesTarget?.target) * 100
                    ) || 0,
                    100
                  )
                  : 0;

                return (
                  <div key={index} className="d-flex align-items-center mb-3">
                    {/* Progress container */}
                    <div
                      className="flex-grow-1 me-3"
                      style={{
                        height: 44,
                        background: "#fff",
                        borderRadius: 10,
                      }}
                    >
                      <OverlayTrigger
                        placement="bottom"
                        overlay={(props) => renderTooltip(props, item, remaining)}
                        trigger={["hover", "focus"]}
                      >
                        <div
                          className="h-100 d-flex align-items-center fw-semibold fs-16 text-white px-3"
                          style={{
                            width: `${percentage}%`,
                            background: getBgColor(percentage),
                            borderRadius: 10,
                            cursor: "pointer",
                          }}
                        >
                          <span
                            title={item.name || ''}
                            style={{
                              display: 'inline-block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: '100%',
                            }}
                          >
                            {item.name || '...'}
                          </span>
                        </div>
                      </OverlayTrigger>
                    </div>

                    {/* Percentage */}
                    <div className="fw-semibold">{percentage}%</div>
                  </div>
                );
              }))}
          </div>
        </div>
      </Col>

      {/* <Col xs={6}>
        <div
          className="px-5 py-5"
          style={{
            border: "0.5px solid #E0E0DF",
            borderRadius: "10px",
            background: "#F9F9F9",
            maxHeight: '230px'
          }}
        >
          <h3 className="fw-bolder fs-18 mb-4 px-2 text-dark">
            Target completion rate
          </h3>
          <div
            id="chart"
            className="d-flex justify-content-center position-relative"
          >
            <Chart
              options={{
                chart: {
                  type: "radialBar",
                  offsetY: -20,
                  sparkline: {
                    enabled: true,
                  },
                },
                plotOptions: {
                  radialBar: {
                    startAngle: -90,
                    endAngle: 90,
                    hollow: {
                      margin: 0,
                      size: "50%",
                      background: "transparent",
                      image: undefined,
                    },
                    track: {
                      background: "#111827", // Dark almost black
                      strokeWidth: "100%",
                      margin: 0,
                      opacity: 1,
                    },
                    dataLabels: {
                      show: true,
                      name: {
                        show: false,
                      },
                      value: {
                        offsetY: -5,
                        fontSize: "32px",
                        fontWeight: 700,
                        color: "#000000",
                        formatter: function (val) {
                          return val + "%";
                        },
                      },
                    },
                  },
                },
                fill: {
                  type: "gradient",
                  gradient: {
                    shade: "dark",
                    type: "horizontal",
                    shadeIntensity: 0.5,
                    gradientToColors: ["#2B43FF"],
                    inverseColors: true,
                    opacityFrom: 1,
                    opacityTo: 1,
                    stops: [0, 100],
                  },
                },
                colors: ["#8492FF"],
                stroke: {
                  lineCap: "butt",
                },
                labels: ["Target completion"],
              }}
              series={series}
              type="radialBar"
              height={350}
            />
          </div>
        </div>
      </Col> */}
    </Row>
  );
};

export default TargetCompletion;
